import { Button, Input, Text } from '@/ui-kit'
import { AxiosError } from 'axios'
import { FormEvent, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { z } from 'zod'
import { useSubmitMissionResult } from '../api'
import type { Mission } from '../api/models'
import { useMissionsUrl } from '../context/MissionsUrlProvider'

function isURL(value: string): boolean {
  if (!value.trim()) return false
  try {
    if (!z.url().safeParse(value).success) return false
    const { protocol } = new URL(value)

    return protocol === 'http:' || protocol === 'https:'
  } catch {
    return false
  }
}

type MissionExternalActionFormProps = {
  mission: Extract<Mission, { type: 'EXTERNAL_ACTION' }>
}

export const MissionExternalActionForm = ({ mission }: MissionExternalActionFormProps) => {
  const { missionsUrl } = useMissionsUrl()

  const [url, setUrl] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const navigate = useNavigate()
  const { mutateAsync: submitMissionResult, isPending } = useSubmitMissionResult()

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setErrorMessage('')

    if (!isURL(url)) {
      setErrorMessage('Enter a valid URL')
      return
    }

    try {
      await submitMissionResult({
        missionId: mission.id,
        submission: { url }
      })
      navigate(missionsUrl)
      toast.success('Mission submitted successfully')
    } catch (err) {
      const error = err as AxiosError
      let message = (error.response?.data as { error?: string })?.error || error.message || 'Failed to submit mission'
      if (message.includes('Submission url already exists')) {
        message = 'This URL has already been used for this mission'
      }
      setErrorMessage(message)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex max-w-[845px] flex-col gap-6">
      <div className="flex flex-col gap-1">
        <Input placeholder="URL" value={url} onChange={setUrl} isDisabled={isPending} />
        {errorMessage && (
          <Text type="body3-regular" className="colorError">
            {errorMessage}
          </Text>
        )}
      </div>
      <Button type="submit" className="max-w-[300px]" isDisabled={isPending || !url.trim()}>
        Submit
      </Button>
    </form>
  )
}
